import { getDatabase, ref as dbRef, get as dbGet } from 'firebase/database'
import { getApp } from 'firebase/app'

// Fallback domains in case Realtime DB node is missing (must match backend defaults)
const DEFAULT_EXCLUDED_DOMAINS = ['@benefitscompliancesolutions.com', '@test.com', '@trotalo.com']

// Module-level cache to avoid repeated RTDB reads (TTL: 5 minutes)
const CACHE_TTL_MS = 5 * 60 * 1000
let cachedDomains = null
let cacheTimestamp = 0

/**
 * Reads excluded email domains from Realtime DB: SYSTEM_PARAMS/EXCLUDED_DOMAINS_ADMIN_CENTER
 * Results are memoized at module scope with a 5-minute TTL.
 * Expected structure: { "0": "@domain1.com", "1": "@domain2.com", ... }
 * @returns {Promise<string[]>} lowercased domain list
 */
export async function getExcludedDomains() {
  if (cachedDomains && (Date.now() - cacheTimestamp) < CACHE_TTL_MS) {
    return cachedDomains
  }

  try {
    const db = getDatabase(getApp())
    const snapshot = await dbGet(dbRef(db, 'SYSTEM_PARAMS/EXCLUDED_DOMAINS_ADMIN_CENTER'))
    const raw = snapshot.val()
    if (!raw || typeof raw !== 'object') {
      cachedDomains = DEFAULT_EXCLUDED_DOMAINS
    } else {
      cachedDomains = Object.values(raw)
        .map(d => String(d).toLowerCase())
        .filter(domain => typeof domain === 'string' && domain.length > 0)
    }
  } catch {
    cachedDomains = DEFAULT_EXCLUDED_DOMAINS
  }

  cacheTimestamp = Date.now()
  return cachedDomains
}

/**
 * Checks whether an email belongs to an excluded domain.
 * @param {string} email
 * @param {string[]} excludedDomains
 * @returns {boolean}
 */
export function isExcludedEmail(email, excludedDomains) {
  if (!email) return false
  const lower = email.toLowerCase()
  return excludedDomains.some(domain => lower.endsWith(domain))
}
